import { Card } from "@/components/ui/card";
import { Clock, HardDrive, Calendar, Check, X, Loader2, Trash2, Film, Scissors, Sparkles, Bot, UserCheck, Copy, Upload, Users } from "lucide-react";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useProjectPolling, useLibraryPolling } from "@/hooks/use-polling";
import { ProcessingStepper } from "@/components/ProcessingStepper";
import { getProjectStatusLabel } from "@/constants/project-status";
import { formatFilesize, formatDuration, formatDateTime } from "@/lib/format";
import { useDeleteProject } from "@/hooks/use-delete-project";
import { useDeleteLibrary } from "@/hooks/use-delete-library";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MyProject } from "@/generated/graphql";
import { OriginalVideoDownloadButton } from "@/components/editor/OriginalVideoDownloadButton";
import ProFeatureDialog from "@/components/ProFeatureDialog";
import { resolveOriginalVideoDownloadUrl } from "@/lib/downloads/resolveOriginalVideoDownloadUrl";
import { useHasFeature } from "@/lib/useHasFeature";
import type { LibraryOrigin } from "@/lib/library/classifyLibraryOrigin";

type ProjectCardItem = MyProject & {
  original_video_url?: string | null;
};

// Utility function to format ETA seconds
const formatEtaTime = (etaSeconds: number | null | undefined): string => {
  if (!etaSeconds || etaSeconds <= 0) return "";

  const minutes = Math.floor(etaSeconds / 60);
  const seconds = Math.round(etaSeconds % 60);

  if (minutes > 0 && seconds > 0) {
    return `${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `${seconds}s`;
  }
};

interface ProjectCardProps {
  project: MyProject;
  type?: 'project' | 'library';
  isCommunity?: boolean;
  origin?: LibraryOrigin;
  onCopyToAccount?: (project: MyProject) => void;
  onDuplicate?: (project: MyProject) => void;
}

export const ProjectCard = ({ project, type = 'project', isCommunity, origin, onCopyToAccount, onDuplicate }: ProjectCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasProAccess = useHasFeature('pro_features');
  // Format the project data
  const durationLabel = formatDuration(project.duration_seconds);
  const sizeLabel = formatFilesize(project.filesize ?? null);
  const dateLabel = formatDateTime(project.createdAt);
  const statusCode = project.curation_status;
  const thumbnail = project.thumbnail_url || undefined;

  const isLibrary = type === 'library';
  const processingMode = (project as any).processing_mode as string | undefined;

  // Use polling hook to get real-time updates when project/library is processing
  const projectPolling = useProjectPolling(
    project.id,
    {
      enabled: !isLibrary && !['success', 'failure', 'expired'].includes(statusCode),
      interval: 5000,
    }
  );
  const libraryPolling = useLibraryPolling(
    project.id,
    {
      enabled: isLibrary && !['success', 'failure', 'expired'].includes(statusCode),
      interval: 5000,
    }
  );
  const { data: polledProject, isFinished, finishedState } = isLibrary ? libraryPolling : projectPolling;

  // Use polled data if available, otherwise fall back to props
  const currentStatusCode = isFinished
    ? (finishedState === 'success' ? 'success' : 'failure')
    : (polledProject?.curation_status || statusCode);

  const currentThumbnail = polledProject?.thumbnail_url || thumbnail;
  const currentName = polledProject?.name || project.name;
  const effectiveStatusLabel = getProjectStatusLabel(polledProject?.curation_status || statusCode);
  const effectiveSizeLabel = polledProject?.filesize ? formatFilesize(polledProject.filesize) : sizeLabel;

  // Get ETA from polled data
  const etaSeconds = polledProject?.eta_seconds;
  const etaTimeStr = formatEtaTime(etaSeconds);
  const etaText = etaTimeStr ? t('projectCard.estimate', { time: etaTimeStr }) : '';

  const isSuccess = currentStatusCode === 'success';
  const isFailure = currentStatusCode === 'failure';
  const isExpired = currentStatusCode === 'expired';

  const getStatusClasses = () => {
    if (isSuccess) {
      return 'bg-gradient-primary bg-clip-text text-transparent font-medium';
    } else if (isFailure) {
      return 'text-red-500';
    } else if (isExpired) {
      return 'text-amber-500';
    }
    return 'text-white';
  };

  const { deleteProject, loading: deletingProject } = useDeleteProject();
  const { deleteLibrary, loading: deletingLibrary } = useDeleteLibrary();
  const deleting = isLibrary ? deletingLibrary : deletingProject;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [proDownloadOpen, setProDownloadOpen] = useState(false);
  const originalDownloadUrl = resolveOriginalVideoDownloadUrl({
    originalVideoUrl: (project as ProjectCardItem).original_video_url,
    cutVideoUrl: project.cut_video_url,
    baseVideoUrl: project.base_video_url,
    compressedBaseVideoUrl: project.compressed_base_video_url,
  });

  const handleThumbnailError = useCallback(() => {
    setThumbnailError(true);
  }, []);

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (isLibrary) {
      await deleteLibrary(project.id);
    } else {
      await deleteProject(project.id);
    }
    setShowDeleteDialog(false);
  };

  const cardContent = (
    <Card className={`relative overflow-hidden p-3 sm:p-4 transition-all duration-300 ${!isExpired ? 'hover:shadow-glow cursor-pointer' : 'cursor-not-allowed'} group`}>
          {!project.public && !isCommunity && (
            <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 transition-opacity ${isExpired ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              {isSuccess && onDuplicate && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDuplicate(project);
                        }}
                        aria-label={t('projectCard.duplicateProject')}
                        className="bg-background/80 hover:bg-background rounded-md p-1.5 text-muted-foreground hover:text-primary shadow-sm"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {t('projectCard.duplicateProject')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {!isLibrary && (
                <button
                  onClick={handleDeleteClick}
                  aria-label={t('projectCard.removeProject')}
                  className="bg-background/80 hover:bg-background rounded-md p-1.5 text-muted-foreground hover:text-destructive shadow-sm"
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          )}
          <div className={`aspect-video bg-gradient-secondary rounded-lg mb-3 overflow-hidden relative ${isExpired ? 'opacity-20' : ''}`}>
            {origin && !isCommunity && (
              <span
                data-testid="library-origin-badge"
                className={`absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide shadow-sm backdrop-blur-sm ${
                  origin === 'community'
                    ? 'bg-background/85 text-foreground ring-1 ring-white/15'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {origin === 'community'
                  ? <Users className="w-3 h-3" />
                  : <Upload className="w-3 h-3" />}
                {origin === 'community'
                  ? t('library.originCommunity')
                  : t('library.originUploaded')}
              </span>
            )}
            {currentThumbnail && !thumbnailError ? (
              <img
                src={currentThumbnail}
                alt={currentName}
                className="w-full h-full object-cover"
                onError={handleThumbnailError}
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">{t('projectCard.noPreview')}</span>
              </div>
            )}
            {currentStatusCode !== 'success' && currentStatusCode !== 'failure' && currentStatusCode !== 'expired' && (
              <div className="absolute inset-0 flex flex-col justify-end p-2.5 bg-gradient-to-t from-black/85 via-black/40 to-transparent backdrop-blur-[1px] pointer-events-none">
                <ProcessingStepper status={currentStatusCode} className="mb-2" />
                <span className="text-white text-xs font-medium leading-tight">{effectiveStatusLabel}</span>
                {etaText && <span className="text-white/60 text-[11px] mt-0.5">{etaText}</span>}
              </div>
            )}
            {isSuccess && !isExpired && originalDownloadUrl && (
              <div
                className="absolute bottom-1.5 right-1.5 z-20"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <OriginalVideoDownloadButton
                  sourceUrl={originalDownloadUrl}
                  hasProAccess={hasProAccess}
                  sourceDuration={project.duration_seconds}
                  appearance="overlay"
                  showExportHint={false}
                  i18nPrefix="projectCard.originalVideoDownload"
                  onProRequired={() => setProDownloadOpen(true)}
                />
              </div>
            )}
          </div>

          <h3 className={`font-medium text-xs sm:text-sm mb-2 group-hover:bg-gradient-primary group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300 ${isExpired ? 'opacity-20' : ''}`}>
            {currentName}
          </h3>

          {(processingMode === 'copilot' || processingMode === 'automatic') && (
            <div className={`mb-2 ${isExpired ? 'opacity-20' : ''}`}>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {processingMode === 'copilot' ? <UserCheck className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                {processingMode === 'copilot' ? t('projectCard.modeCopilot') : t('projectCard.modeAutomatic')}
              </span>
            </div>
          )}

          <div className={`space-y-1 text-xs text-muted-foreground ${isExpired ? 'opacity-20' : ''}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {durationLabel}</span>
              <span className="inline-flex items-center gap-1"><HardDrive className="w-3.5 h-3.5" /> {effectiveSizeLabel}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateLabel}</span>
              <div className="flex flex-col items-end gap-0.5">
                <span className={`inline-flex items-center gap-1 ${getStatusClasses()}`}>
                  {isSuccess ? <Check className="w-3.5 h-3.5" /> : null}
                  {isFailure ? <X className="w-3.5 h-3.5" /> : null}
                  {isExpired ? <X className="w-3.5 h-3.5" /> : null}
                  {effectiveStatusLabel}
                </span>
                {etaText && !isSuccess && !isFailure && !isExpired && (
                  <span className="text-muted-foreground text-xs">{etaText}</span>
                )}
              </div>
            </div>
          </div>

          {isCommunity ? (
            <div className="flex flex-col gap-2 mt-3">
              <Button
                variant="gradient"
                size="sm"
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToAccount?.(project);
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('library.generateAIClips')}
              </Button>
            </div>
          ) : isLibrary && isSuccess && (
            <TooltipProvider>
              <div className="flex flex-col gap-2 mt-3">
                {(project.clips_count ?? 0) > 0 ? (
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <RouterLink
                      to={`/${type}/${project.id}/clips`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Film className="w-4 h-4 mr-2" />
                      {t('projectCard.viewExportedClips')}
                    </RouterLink>
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-full">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          disabled
                        >
                          <Film className="w-4 h-4 mr-2" />
                          {t('projectCard.viewExportedClips')}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('projectCard.exportedClipsTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                  {(project as any).processing_mode === 'copilot' && (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <RouterLink
                        to={`/library/${project.id}/edit`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Scissors className="w-4 h-4 mr-2" />
                        {t('projectCard.editClipsManually')}
                      </RouterLink>
                    </Button>
                  )}
              </div>
            </TooltipProvider>
          )}
        </Card>
  );

  return (
    <>
      {isExpired ? (
        cardContent
      ) : isCommunity ? (
        <RouterLink to={
          isSuccess && (project as any).processing_mode === 'copilot'
            ? `/library/${project.id}/edit`
            : `/library/${project.id}/clips`
        }>
          {cardContent}
        </RouterLink>
      ) : (
        <RouterLink to={
          isLibrary
            ? (isSuccess && (project as any).processing_mode === 'copilot'
                ? `/library/${project.id}/edit`
                : `/library/${project.id}/clips`)
            : (isSuccess && (project as any).processing_mode === 'copilot'
                ? `/project/${project.id}/edit`
                : `/project/${project.id}/clips`)
        }>
          {cardContent}
        </RouterLink>
      )}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projectCard.removeProject')}</DialogTitle>
            <DialogDescription>
              {t('projectCard.removeProjectConfirm')}
              {!isFailure && (
                <span className="block mt-2 text-amber-600 dark:text-amber-400 font-medium">
                  ⚠️ {t('projectCard.removeProjectWarning')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false)
              }}
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('projectCard.removing')}
                </>
              ) : (
                t('projectCard.remove')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProFeatureDialog
        open={proDownloadOpen}
        onOpenChange={setProDownloadOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('projectCard.originalVideoDownload.proTitle')}
        body={t('projectCard.originalVideoDownload.proBody')}
        dismissLabel={t('projectCard.originalVideoDownload.proDismiss')}
        upgradeLabel={t('projectCard.originalVideoDownload.proUpgrade')}
        testIdPrefix="project-original-download-pro"
      />
    </>
  );
};
